import { createSelector } from 'reselect';
import { parse } from 'qs';
import { OrderedSet } from 'immutable';
import pick from 'lodash.pick';
import isEmpty from 'lodash.isempty';

import { getFilterParamsAdapter, getTimeFilterParams } from '../../../api-adapter';
import * as util from '../../../util';
import { convertOwnersV1ToV2, convertOwnersV2ToV1, setupFiltersQuery, getMyselfOwnerOption, defaultParseQueryParam, KEY_TO_PARSER_MAP, FILTER_PARAMS_MAPPINGS, FILTER_PARAMS_QUERY_MAPPINGS } from './utils';
import { OWNER_PARAM_MAP, OWNER_PARAM_VALUE_MAP } from './constants';

export const getUser = (store) => store.user;
export const getLocation = (store) => store.router.location;

export const getBasicOwners = createSelector([getUser],
  (userState) => {
    const owners = [getMyselfOwnerOption(userState)];

    if (util.isOrganizationScopeEnabled(userState)) {
      owners.push({
        name: userState.organization.id.toString(),
        label: 'My Organization',
        type: 'org',
        ownerV1: 'organization',
      });
    }

    owners.push(...userState.teams.map((team) => ({
      name: team.id,
      label: `My Team (${team.name})`,
      type: 'team',
      ownerV1: 'team',
    })));

    return owners;
  });

export const getDefaultOwners = createSelector([getUser], (userState) => {
  const defaultOwners = [];
  if (!util.isOrganizationScopeEnabled(userState)) {
    defaultOwners.push(getMyselfOwnerOption(userState));
  }

  return defaultOwners;
});

export const createGetFiltersParams = (
  getFilters,
  filterKeys,
) => createSelector(
  [getFilters, getUser, (store, options) => options],
  (filtersState, userState, options) => ({
    ...getFilterParamsAdapter(
      filtersState.get('filters').filter((value, key) => filterKeys.has(key)),
      FILTER_PARAMS_MAPPINGS,
      {},
    ),
    ...getTimeFilterParams(
      filtersState.get('selectedDateFilter'),
      Date.now(),
      options?.priorData,
    ),
    org_id: userState.organization.id,
  }),
);

export const createGetFiltersQuery = (
  getFilters,
  queryMapping = FILTER_PARAMS_QUERY_MAPPINGS,
) => createSelector(
  [getFilters, getLocation],
  (filtersState, location) => (payload, method) => {
    const query = method(payload, location, {
      mapping: queryMapping,
      state: filtersState,
    });
    const ownersFilters = filtersState.getIn([
      'filters',
      'owner',
      'selectedItems',
    ]);
    const filtersQuery = {
      ...getFilterParamsAdapter(
        filtersState.get('filters'),
        queryMapping,
        {},
      ),
      owners: convertOwnersV2ToV1(query, ownersFilters),
      dateFilter: JSON.stringify(filtersState.get('selectedDateFilter')),
    };

    return { filtersQuery, query };
  },
);

export const createGetFiltersAsQueryObject = (getFilters, getFiltersQuery) => (
  createSelector([getFilters, getFiltersQuery], (filtersState, filtersQueryGetter) => {
    const { query } = filtersQueryGetter(filtersState, setupFiltersQuery);
    return query;
  }));

export const createGetFetchFiltersParams = (
  getFiltersParams,
  keys = ['user_id', 'team_id', 'org_id', 'group_id', 'start', 'end'],
) => (
  createSelector([getFiltersParams], (filtersParams) => pick(filtersParams, keys))
);

export const createFiltersSelector = (getFilters) => createSelector([
  getFilters,
], (filtersState) => ({
  filters: filtersState.get('filters'),
  selectedDateFilter: filtersState.get('selectedDateFilter'),
}));

export const createGetParsedFiltersFromURL = (getFilters) => (
  createSelector(
    [getLocation,
      getFilters,
      (store, options) => options],
    (location, filtersState, { defaultOwners = [] } = {}) => {
      const { search = '' } = location;

      const storageKey = util.getStorageKey(location.pathname);
      const filtersFromStorage = window.sessionStorage.getItem(storageKey);
      const parsedQueryString = parse(search, { ignoreQueryPrefix: true, arrayLimit: 100 });
      const parsedQueryStringWithOwners = {
        ...parsedQueryString,
        ...convertOwnersV1ToV2(parsedQueryString, OWNER_PARAM_VALUE_MAP, filtersState.getIn(['filters', 'owner', 'items'])),
      };
      const storedFilters = isEmpty(parsedQueryStringWithOwners) ? parse(filtersFromStorage) :
        parsedQueryStringWithOwners;
      const isUserSelected = Object.keys(storedFilters).includes(OWNER_PARAM_MAP.user);

      const parsedFilters = Object.entries(storedFilters).reduce((output, [key, value]) => {
        const parseQueryToFilter = KEY_TO_PARSER_MAP[key] ?? defaultParseQueryParam;
        return parseQueryToFilter(key, value, output, { isUserSelected });
      }, filtersState);

      if (parsedFilters.getIn(['filters', 'owner', 'selectedItems']).isEmpty()) {
        return parsedFilters.setIn(['filters', 'owner', 'selectedItems'], OrderedSet(defaultOwners));
      }

      return parsedFilters;
    })
);

export const createGetUsersParams = (getFilters) => createSelector([getUser, getFilters],
  (user, filterState) => {
    const searchValue = filterState.getIn(['filters', 'owner', 'searchValue']) ?? null;
    return {
      isIndividualUserAllowed: user.isOrgAdmin() || user.isJobTeamSharingEnabled(),
      queryParams: {
        ...(searchValue ? { phrase: searchValue } : {}),
      },
    };
  });

export const createGetMetadataParams = (
  getFiltersParams,
) => (
  createSelector([
    getFiltersParams,
    (store, options = {}) => options,
  ], (filtersParams, { value, filterKey }) => ({
    ...filtersParams,
    [filterKey]: value,
  }))
);
